/**
 * BprDealArchitecture - the deal-embedded System Architecture section
 * (BPR-category deals).
 *
 * Per the BPR workflow, the FIRST parsed-out section is always the high-level
 * System Architecture: the current state, with the future state as a second
 * tab inside the section. It renders from the same cached diagram batch every
 * section embed shares (dealDiagramsStore); when nothing is generated yet it
 * carries the generation entry point (build the knowledge graph over the
 * uploaded transcripts, then generate the visual layer) so the whole BPR
 * flow runs from within the deal page - no separate engagement page.
 *
 * Self-gating: non-BPR deals resolve no engagement and render nothing.
 */

import type { BprDiagramNode, BprDiagramState } from '@protoss/shared';
import { Button, Pill } from '@protoss/ui';
import * as React from 'react';
import { bprApi, type BprGraphDiagramResult } from '../services/bprApi';
import {
  generateDealDiagrams,
  useDealDiagrams,
  type DealDiagramsGenerationPhase,
} from '../services/dealDiagramsStore';
import { BprDiagramCanvas } from './BprDiagramCanvas';
import { BprDiagramLegend } from './BprDiagramLegend';
import { BprGraphPriorityView } from './BprGraphPriorityView';
import { BprGraphSystemView } from './BprGraphSystemView';

// Mechanics: expected per-phase durations calibrating the progress meter (the
// diagram batch runs parallel calls on the fast tier). The bar holds at its
// phase ceiling until the run actually completes - it is an expectation
// meter, not a measured stream.
const PHASE_EXPECTED_MS: Record<DealDiagramsGenerationPhase, number> = {
  graph: 75_000,
  diagrams: 150_000,
};
const PHASE_LABEL: Record<DealDiagramsGenerationPhase, string> = {
  graph: 'Step 1 of 2 - building the knowledge graph from the discovery transcripts',
  diagrams: 'Step 2 of 2 - drawing the architecture + per-section process flows (current & future)',
};

function formatElapsed(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Phase-weighted meter: graph fills 0-45%, diagrams 45-95%, done snaps off. */
function GenerationProgress({
  phase,
  startedAt,
  phaseStartedAt,
}: {
  phase: DealDiagramsGenerationPhase;
  startedAt: number;
  phaseStartedAt: number;
}) {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  const phaseElapsed = Math.max(0, now - phaseStartedAt);
  const pct =
    phase === 'graph'
      ? Math.min(44, (phaseElapsed / PHASE_EXPECTED_MS.graph) * 45)
      : 45 + Math.min(50, (phaseElapsed / PHASE_EXPECTED_MS.diagrams) * 50);
  return (
    <div className="bpr-deal-arch__progress" role="status" aria-live="polite">
      <div
        className="bpr-deal-arch__progress-bar"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pct)}
        aria-label="Diagram generation progress"
      >
        {/* Dynamic dimension only (same pattern as the wizard's sidebar width). */}
        <div className="bpr-deal-arch__progress-fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="bpr-deal-arch__progress-label">{PHASE_LABEL[phase]}</span>
      <span className="bpr-deal-arch__progress-time">
        Elapsed {formatElapsed(now - startedAt)} - a v1 usually lands in 2-4 minutes.
      </span>
    </div>
  );
}

/**
 * Sentinel sectionKey for the display-only "System Architecture" section the
 * deal questionnaire pins after the Company section (BPR deals). Never
 * persisted - it exists only in the display questionnaire / section nav.
 */
export const BPR_ARCHITECTURE_SECTION_KEY = '__bpr_architecture__';

export interface BprDealArchitectureProps {
  dealUuid: string;
  /** Non-viewers can build the graph + (re)generate the diagram batch. */
  canGenerate?: boolean;
  /** Hide the block's own heading (the wizard pane already titles the step). */
  hideTitle?: boolean;
}

export function BprDealArchitecture({
  dealUuid,
  canGenerate = false,
  hideTitle = false,
}: BprDealArchitectureProps) {
  const {
    status,
    engagementUuid,
    diagrams,
    generating,
    generationPhase,
    generationStartedAt,
    generationPhaseStartedAt,
    error,
  } = useDealDiagrams(dealUuid);
  const [state, setState] = React.useState<BprDiagramState>('current');
  const [selectedNode, setSelectedNode] = React.useState<BprDiagramNode | null>(null);
  // The engagement's knowledge-graph diagram; its priority_view (the inverted
  // triangle) renders under the architecture canvas. Fetched here rather than
  // in the shared store since this pinned section is its only consumer.
  const [graphDiagram, setGraphDiagram] = React.useState<BprGraphDiagramResult | null>(null);

  React.useEffect(() => {
    if (!engagementUuid) {
      setGraphDiagram(null);
      return;
    }
    let cancelled = false;
    // Re-fetch when the diagram batch changes (a regenerate rebuilds the graph).
    void bprApi
      .getGraphDiagram(engagementUuid)
      .then((result) => {
        if (!cancelled) setGraphDiagram(result);
      })
      .catch(() => {
        // Best-effort: the triangle is supplementary, never block the section.
      });
    return () => {
      cancelled = true;
    };
  }, [engagementUuid, diagrams]);

  const progressNode =
    generating && generationPhase && generationStartedAt && generationPhaseStartedAt ? (
      <GenerationProgress
        phase={generationPhase}
        startedAt={generationStartedAt}
        phaseStartedAt={generationPhaseStartedAt}
      />
    ) : null;

  const architecture = React.useMemo(
    () => diagrams.filter((d) => d.kind === 'system-architecture'),
    [diagrams]
  );

  // Not a BPR deal (or not resolved yet): stay out of the questionnaire.
  if (status !== 'ready' || !engagementUuid) return null;

  if (architecture.length === 0) {
    // The generation entry point lives here so the flow starts from the deal.
    // Generation kicks off automatically once discovery questions are
    // captured (DealWorkspace's auto-generate effect); the button remains as
    // the manual fallback/retry.
    if (!canGenerate) return null;
    return (
      <div
        className="bpr-deal-arch bpr-deal-arch--empty"
        data-section-key={BPR_ARCHITECTURE_SECTION_KEY}
      >
        {hideTitle ? null : <h2 className="bpr-deal-arch__title">System Architecture</h2>}
        {progressNode ?? (
          <p className="bpr-deal-arch__empty-text">
            Diagrams generate automatically once discovery questions are captured. You can also run
            it now: generating builds the knowledge graph over the uploaded transcripts, then
            creates the system architecture (current + future state) and a process flow per section.
          </p>
        )}
        {error ? <p className="bpr-deal-arch__error">{error}</p> : null}
        {generating ? null : (
          <div>
            <Button type="button" size="sm" onClick={() => void generateDealDiagrams(dealUuid)}>
              Generate diagrams
            </Button>
          </div>
        )}
      </div>
    );
  }

  const active =
    architecture.find((d) => (d.state ?? 'current') === state) ?? architecture[0] ?? null;

  return (
    <div className="bpr-deal-arch" data-section-key={BPR_ARCHITECTURE_SECTION_KEY}>
      <div className="bpr-deal-arch__head">
        {hideTitle ? (
          <span className="bpr-deal-arch__head-spacer" aria-hidden="true" />
        ) : (
          <h2 className="bpr-deal-arch__title">System Architecture</h2>
        )}
        <div
          className="bpr-section-diagram__state-toggle"
          role="tablist"
          aria-label="Architecture state"
        >
          {(['current', 'future'] as const).map((s) => (
            <button
              key={s}
              type="button"
              role="tab"
              aria-selected={state === s}
              className={`bpr-section-diagram__state-btn${
                state === s ? ' bpr-section-diagram__state-btn--active' : ''
              }`}
              onClick={() => {
                setState(s);
                setSelectedNode(null);
              }}
            >
              {s === 'current' ? 'Current state' : 'Future state'}
            </button>
          ))}
        </div>
        {canGenerate ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={generating}
            onClick={() => void generateDealDiagrams(dealUuid, true)}
          >
            {generating ? 'Regenerating…' : 'Regenerate'}
          </Button>
        ) : null}
      </div>
      {error ? <p className="bpr-deal-arch__error">{error}</p> : null}
      {progressNode}
      {active ? (
        <>
          <BprDiagramCanvas
            key={active.uuid + state}
            spec={active.spec}
            onSelectNode={setSelectedNode}
            exportTitle={active.title}
          />
          <BprDiagramLegend />
        </>
      ) : null}
      {selectedNode ? (
        <div className="bpr-section-diagram__issues">
          <span className="bpr-section-diagram__issues-title">{selectedNode.label}</span>
          <Pill variant="ghost">{selectedNode.system}</Pill>
          <Pill variant="neutral">{selectedNode.status}</Pill>
          {selectedNode.issues.length === 0 ? (
            <span className="bpr-section-diagram__issues-empty">no linked issues</span>
          ) : (
            <ul className="bpr-section-diagram__issues-list">
              {selectedNode.issues.map((issue, idx) => (
                <li key={idx}>{issue}</li>
              ))}
            </ul>
          )}
        </div>
      ) : null}

      {/* Below current/future state: the knowledge graph's own D1 views for
          this engagement - the system-architecture map and the inverted
          triangle (prioritized findings). */}
      {graphDiagram && graphDiagram.system_view.nodes.length > 0 ? (
        <section className="bpr-graph__panel bpr-deal-arch__triangle">
          <h3 className="bpr-section__subheading">System map</h3>
          <p className="bpr-graph__hint">
            Layer 1 of the triangle: the systems the client runs and the integration points between
            them. Dashed red edges are integration gaps; badges count the findings on a system.
          </p>
          <BprGraphSystemView systemView={graphDiagram.system_view} exportTitle="System map" />
        </section>
      ) : null}
      {graphDiagram && graphDiagram.priority_view.items.length > 0 ? (
        <section className="bpr-graph__panel bpr-deal-arch__triangle">
          <h3 className="bpr-section__subheading">Prioritized findings</h3>
          <p className="bpr-graph__hint">
            The knowledge graph's inverted triangle: findings bucketed into deterministic Critical /
            High / Medium / Low tiers (apex first).
          </p>
          <BprGraphPriorityView priorityView={graphDiagram.priority_view} />
        </section>
      ) : null}
    </div>
  );
}
